import { type BrandName, ElithairZohoTypes } from "@elit-hair/brands";
import { PaginatedResponse } from "@elit-hair/elit-tools";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { DateTime } from "luxon";

import { callSvcAxiosInstance } from "@/constants";
import { callServiceEndpoints } from "@/constants/endpoints";
import { LEAD_ACTION, LEAD_RESOLUTIONS, type LeadBlockingStateType } from "@/constants/lead.constants";
import { getLeadListFilterKey } from "@/pages/lists/utils/lead-list-filter.utils";
import { LeadListFilter } from "@/types/lead-list-filter.types";

export const leadKeys = {
    all: ["leads"] as const,
    leads: (filter?: LeadListFilter) => ["leads", filter ? getLeadListFilterKey(filter) : undefined] as const,
    statuses: ["leads", "statuses"] as const,
};

export type PriorityRecordCount = {
    count: number;
    /** Setting list key echoed from the request filter.key. */
    language: string;
    priority: number;
};

export type PriorityRecordCountFilter = {
    key: string;
    languages: string[];
    layouts?: string[];
    statuses?: string[];
    settingPriorityMin?: number;
    settingPriorityMax?: number;
};

export type GetPrioRecordCountsParams = {
    start?: string;
    end?: string;
    filters: PriorityRecordCountFilter[];
};

const getPriorityRecordCounts = async (params: GetPrioRecordCountsParams): Promise<PriorityRecordCount[]> => {
    const response = await callSvcAxiosInstance.post("/lead/prio-record-counts", params);
    return response.data;
};

export const usePriorityRecordCounts = (params: GetPrioRecordCountsParams, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ["priority-record-counts", params],
        queryFn: () => getPriorityRecordCounts(params),
        enabled: (options?.enabled ?? true) && params.filters.length > 0,
        refetchInterval: 10000,
    });
};

const getLeadStatuses = async (): Promise<string[]> => {
    const response = await callSvcAxiosInstance.get<{ data: string[] }>(callServiceEndpoints.getLeadStatuses);
    return response.data.data;
};

export const useLeadStatuses = (options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: leadKeys.statuses,
        queryFn: getLeadStatuses,
        enabled: options?.enabled ?? true,
        staleTime: 60_000,
    });
};

export const useLeads = (filter: LeadListFilter | null | undefined, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: leadKeys.leads(filter ?? undefined),
        queryFn: () => {
            if (!filter) throw new Error("Filter is required");
            return getAndAssignLeads(filter);
        },
        enabled: (options?.enabled ?? true) && !!filter?.languages.length,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
    });
};

export type LeadWithAssignment = {
    id: string;
    zohoLeadId: string;
    orgId: string;
    email: ElithairZohoTypes["Leads"]["Email"];
    phone: ElithairZohoTypes["Leads"]["Phone"];
    mobile: ElithairZohoTypes["Leads"]["Mobile"];
    settingPriority: number | null;
    assigneeId: string | null;
    status: ElithairZohoTypes["Leads"]["Lead_Status"];
    fullName: ElithairZohoTypes["Leads"]["Full_Name"];
    salutation?: string;
    language: ElithairZohoTypes["Leads"]["Sprache"];
    image1: string | null;
    image2: string | null;
    image3: string | null;
    createdAt: DateTime;
    updatedAt: DateTime;
    blockedUntil: DateTime | null;
    assignmentExpiresAt?: DateTime | null;
    recentCallsCount: number;
    layout: string | null;
};

type LeadWithAssignmentRaw = Omit<
    LeadWithAssignment,
    "createdAt" | "updatedAt" | "assignmentExpiresAt" | "settingPriority" | "blockedUntil"
> & {
    createdAt: string;
    updatedAt: string;
    assignmentExpiresAt?: string | null;
    blockedUntil?: string | null;
    settingPriority?: number | null;
};

const getAndAssignLeads = async (filter: LeadListFilter): Promise<LeadWithAssignment[]> => {
    const response = await callSvcAxiosInstance.post<LeadWithAssignmentRaw[]>(
        callServiceEndpoints.getAndAssignLeads,
        filter
    );

    return response.data.map(transformLeadsDates);
};

const unassignLeads = async (): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.unassignLeads);
};

const adminUnassignLeads = async (userId: string): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.adminUnassignLeads(userId));
};

const resolveLead = async (id: string, resolution: LEAD_RESOLUTIONS): Promise<void> => {
    await callSvcAxiosInstance.post(callServiceEndpoints.resolveLead(id), { resolution });
};

const triggerLeadAction = async (zohoLeadId: string, action: LEAD_ACTION): Promise<void> => {
    await callSvcAxiosInstance.post(callServiceEndpoints.leadAction(zohoLeadId), { action });
};

export type HideLeadUntilParams = {
    leadId: string;
    hideUntilDate: string;
};

const hideLeadUntil = async ({ leadId, hideUntilDate }: HideLeadUntilParams): Promise<void> => {
    await callSvcAxiosInstance.post(callServiceEndpoints.hideLead(leadId), { hideUntilDate });
};

const unassignLead = async (id: string): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.unassignLead(id));
};

const deleteLeadByZohoId = async ({
    brandName,
    zohoLeadId,
}: {
    brandName: BrandName;
    zohoLeadId: string;
}): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.deleteLead(brandName, zohoLeadId));
};

export const useUnassignLeads = () => {
    return useMutation({
        mutationFn: unassignLeads,
    });
};

export const useAdminUnassignLeads = (userId: string) => {
    return useMutation({
        mutationFn: () => adminUnassignLeads(userId),
    });
};

export const useUnassignLead = () => {
    return useMutation({
        mutationFn: unassignLead,
    });
};

export const useDeleteLead = () => {
    return useMutation({
        mutationFn: deleteLeadByZohoId,
    });
};

export const useResolveLead = () => {
    return useMutation({
        mutationFn: ({ id, resolution }: { id: string; resolution: LEAD_RESOLUTIONS }) => resolveLead(id, resolution),
    });
};

export const useTriggerLeadAction = () => {
    return useMutation({
        mutationFn: ({ zohoLeadId, action }: { zohoLeadId: string; action: LEAD_ACTION }) =>
            triggerLeadAction(zohoLeadId, action),
    });
};

export const useHideLeadUntil = () => {
    return useMutation({
        mutationFn: hideLeadUntil,
    });
};

const getLeadsByPriority = async (
    priority: number,
    filter: LeadListFilter,
    page?: number,
    limit?: number,
    getBlockedLeads?: LeadBlockingStateType
): Promise<PaginatedResponse<LeadWithAssignment>> => {
    const response = await callSvcAxiosInstance.post<PaginatedResponse<LeadWithAssignmentRaw>>("/lead/by-priority", {
        priority,
        ...filter,
        ...(page && { page }),
        ...(limit && { limit }),
        ...(getBlockedLeads !== undefined ? { blockingState: getBlockedLeads } : {}),
    });

    // Transform dates to Luxon DateTime objects (backend always returns ISO strings)
    return {
        data: response.data.data.map(transformLeadsDates),
        pagination: response.data.pagination,
    };
};

type ExportLeadsByPriorityParams = {
    priority: number;
    filter: LeadListFilter;
    blockingState?: LeadBlockingStateType;
};

const exportLeadsByPriority = async ({
    priority,
    filter,
    blockingState,
}: ExportLeadsByPriorityParams): Promise<Blob> => {
    const response = await callSvcAxiosInstance.post<Blob>(
        "/lead/by-priority/export",
        {
            priority,
            ...filter,
            ...(blockingState !== undefined ? { blockingState } : {}),
        },
        {
            responseType: "blob",
        }
    );

    return response.data;
};

export const useExportLeadsByPriority = () => {
    return useMutation({
        mutationFn: exportLeadsByPriority,
    });
};

export const useLeadsByPriority = (
    priority: number,
    filter: LeadListFilter | null,
    page?: number,
    limit?: number,
    getBlockedLeads?: LeadBlockingStateType
) => {
    return useQuery({
        queryKey: ["leads-by-priority", priority, filter, page, limit, getBlockedLeads],
        queryFn: () => {
            if (!filter) throw new Error("Filter is required");
            return getLeadsByPriority(priority, filter, page, limit, getBlockedLeads);
        },
        enabled: !!priority && !!filter?.languages.length,
        // Avoid unexpected refetch (and full-page loading) when window regains focus,
        // e.g. after opening or saving an exported file.
        refetchOnWindowFocus: false,
    });
};

const refreshLeadFromZoho = async (zohoLeadId: string): Promise<void> => {
    await axios.get("https://n8n.elithair.tech/webhook/setting-priorities-sync", {
        params: {
            leadId: zohoLeadId,
        },
    });
};

export const useRefreshLeadFromZoho = () => {
    return useMutation({
        mutationFn: refreshLeadFromZoho,
    });
};

// Transform dates to Luxon DateTime objects (backend always returns ISO strings)
const transformLeadsDates = (lead: LeadWithAssignmentRaw): LeadWithAssignment => {
    const createdAt = DateTime.fromISO(lead.createdAt);
    const updatedAt = DateTime.fromISO(lead.updatedAt);
    const assignmentExpiresAt = lead.assignmentExpiresAt ? DateTime.fromISO(lead.assignmentExpiresAt) : null;
    const blockedUntil = lead.blockedUntil ? DateTime.fromISO(lead.blockedUntil) : null;

    if (!createdAt.isValid) {
        console.warn(`Invalid createdAt date for lead ${lead.id}: ${lead.createdAt}`);
    }
    if (!updatedAt.isValid) {
        console.warn(`Invalid updatedAt date for lead ${lead.id}: ${lead.updatedAt}`);
    }
    if (assignmentExpiresAt && !assignmentExpiresAt.isValid) {
        console.warn(`Invalid assignmentExpiresAt date for lead ${lead.id}: ${lead.assignmentExpiresAt}`);
    }
    if (blockedUntil && !blockedUntil.isValid) {
        console.warn(`Invalid blockedUntil date for lead ${lead.id}: ${lead.blockedUntil}`);
    }

    return {
        ...lead,
        settingPriority: lead.settingPriority ?? null,
        layout: lead.layout ?? null,
        recentCallsCount: lead.recentCallsCount ?? 0,
        createdAt: createdAt.isValid ? createdAt : DateTime.now(),
        updatedAt: updatedAt.isValid ? updatedAt : DateTime.now(),
        blockedUntil: blockedUntil?.isValid ? blockedUntil : null,
        assignmentExpiresAt: assignmentExpiresAt?.isValid ? assignmentExpiresAt : null,
    };
};
